import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Api from '../../services/api.service';
import Helper from '../../services/helper.service';
import Loading from '../../components/Loading/loading';

import DateFieldInput from '../../components/Inputs/date-field';
import AccommodationDisplayed from '../Reservation/components/accommodation-displayed';
import Select from '../../components/Inputs/select';

import _ from 'lodash';
import moment from 'moment';
import { useTranslation } from "react-i18next";
import { faUser } from '@fortawesome/free-solid-svg-icons';
import H2 from '../../components/Titles/h2';
import DescriptionSplit from '../../components/Description/description-split';
import Link from '../../components/Links/link';
import Button from '../../components/Buttons/button';
import LogoLong from '../../components/Logos/logo-long';

// ---------- Helpers ----------
const euro = (n) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const asNumber = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

const euroShort = (n) => {
    const amount = Number(n) || 0;
    const isInt = Number.isInteger(amount);

    const options = isInt
        ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        ...options,
    }).format(amount);
};

// price display helpers (for services/options)
function deriveHTTTC({ price, publicPrice, vat, mode, t }) {
    const VAT = asNumber(vat, 0);
    const mult = 1 + VAT / 100;

    let base = price;
    if (mode === 'PUBLIC_TTC' || mode === 'PUBLIC_HT') {
        base = publicPrice ?? price;
    }

    const TTC = mode === 'PUBLIC_HT' ? asNumber(base) * mult : asNumber(base);
    const HT = VAT ? TTC / mult : TTC;

    const euroHT = euroShort(HT);
    const euroTTC = euroShort(TTC);

    const m = mode;

    const htLabel = t('businessProposal.prices.ht', { value: euroHT });     // "150,00 € HT"
    const ttcLabel = t('businessProposal.prices.ttc', { value: euroTTC });  // "150,00 € TTC"

    let label = '';
    let secondaryLabel = '';

    switch (m) {
        case 'HT':
            label = htLabel;
            break;

        case 'HT_TTC':
        case 'BOTH_HT_FIRST':
            label = htLabel;
            secondaryLabel = ttcLabel;
            break;

        case 'BOTH_TTC_FIRST':
            label = ttcLabel;
            secondaryLabel = htLabel;
            break;

        case 'HIDE':
            // nothing visible
            label = '';
            secondaryLabel = '';
            break;

        case 'PUBLIC_HT':
            label = htLabel;
            break;

        case 'PUBLIC_TTC':
        case 'TTC':
        default:
            label = ttcLabel;
            break;
    }

    return { label, secondaryLabel, HT, TTC };
}

const Section = React.forwardRef(({ title, subtitle, children, ...rest }, ref) => (
    <div ref={ref} className="mb-8 scroll-mt-32" {...rest}>
        <div className="mb-3">
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight flex items-center gap-2">
                {title}
            </h2>
            {subtitle && (
                <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
        </div>
        <div>{children}</div>
    </div>
));

Section.displayName = 'Section';

const Card = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full text-left p-4 md:p-4 border transition shadow-sm hover:shadow-md focus:outline-none ${active ? 'border-main ring-2 ring-main/10 bg-white' : 'border-gray-200 bg-white'
            }`}
    >
        <div className="flex items-start">{children}</div>
    </button>
);

const PricePill = ({ primary, secondary }) => {
    if (!primary) return null;

    return (
        <span className="inline-flex flex-col items-end border-gray-200 px-3 py-1 text-right leading-tight">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {primary}
            </span>
            {secondary && (
                <span className="text-xs text-gray-400 whitespace-nowrap">
                    {secondary}
                </span>
            )}
        </span>
    );
};

const serializeGroupSelections = (groupSelections = {}) => {
    const out = {};
    Object.entries(groupSelections).forEach(([groupId, value]) => {
        if (value instanceof Set) {
            out[groupId] = Array.from(value);
        } else {
            out[groupId] = value ?? null;
        }
    });
    return out;
};

// ---------- Main Component ----------
export default function BusinessProposalPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [proposal, setProposal] = useState(null);
    const [error, setError] = useState(null);
    const [afterSaveView, setAfterSaveView] = useState(false);
    const [savedQuotation, setSavedQuotation] = useState(null);

    // selections
    const [accommodation, setAccommodation] = useState(''); // 'domain' | 'home' | 'annex' | ''
    const [selectedServices, setSelectedServices] = useState(new Set()); // ids
    const [groupSelections, setGroupSelections] = useState({}); // groupId -> id | Set
    const [quantities, setQuantities] = useState({});

    // number of guests for display / tax / limits
    const [nbAdults, setNbAdults] = useState(2);
    const [nbChilds, setNbChilds] = useState(0);

    const MAX_PEOPLE_DOMAIN = 22;
    const MAX_PEOPLE_HOME = 12;
    const MAX_PEOPLE_ANNEX = 10;

    const [isSaving, setIsSaving] = useState(false);
    const isSavingRef = useRef(false);

    // dates + pricing
    const twoYearsLater = new Date(new Date().setFullYear(new Date().getFullYear() + 2));
    const [stayDates, setStayDates] = useState({ startDate: null, endDate: null });
    const [blockedDates, setBlockedDates] = useState([]);
    const [cheaperDates, setCheaperDates] = useState([]);
    const [promoDates, setPromoDates] = useState([]);
    const [prices, setPrices] = useState({
        annex: { price: 0, base: 0, cleaning: 0, promotion: 0 },
        home: { price: 0, base: 0, cleaning: 0, promotion: 0 },
        domain: { price: 0, base: 0, cleaning: 0, promotion: 0 },
    });

    const lastPriceReqRef = useRef(null);
    const groupRefs = useRef({});

    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isDescriptionClamped, setIsDescriptionClamped] = useState(false);
    const descriptionRef = useRef(null);

    // ---------- i18n helpers tied to keys ----------
    const longAccommodationName = useCallback(
        (key) => {
            const map = {
                domain: t('businessProposal.accommodation.names.domainLong'),
                home: t('businessProposal.accommodation.names.homeLong'),
                annex: t('businessProposal.accommodation.names.annexLong'),
            };
            return map[key] || '—';
        },
        [t]
    );

    // ---------- Load proposal ----------
    useEffect(() => {
        let mounted = true;

        const resolveTitle = (ref, lang = 'fr', fallbackBase) => {
            const d = ref?.displayName;
            if (d?.[lang]) return String(d[lang]);
            if (d?.fr || d?.en || d?.de) return String(d.fr || d.en || d.de);

            const c = ref?.custom?.name;
            if (c?.[lang]) return String(c[lang]);
            if (c?.fr || c?.en || c?.de) return String(c.fr || c.en || c.de);

            const svc = ref?.service;
            const sname = svc?.name;
            if (sname?.[lang]) return String(sname[lang]);
            if (sname?.fr || sname?.en || sname?.de || svc?.slug) {
                return String(sname?.fr || sname?.en || sname?.de || svc?.slug);
            }
            return fallbackBase;
        };

        const resolveNote = (ref, lang = 'fr') => {
            const n = ref?.note;
            if (n?.[lang]) return String(n[lang]);
            if (n?.fr || n?.en || n?.de) return String(n.fr || n.en || n.de);
            return '';
        };

        const resolvePrice = (ref) => Number(ref?.overridePrice ?? ref?.custom?.price ?? 0);
        const resolvePublic = (ref) => Number(ref?.overridePublicPrice ?? ref?.custom?.publicPrice ?? NaN);
        const resolveVAT = (ref) => {
            const v = ref?.overrideVAT ?? ref?.custom?.VAT;
            return Number.isFinite(Number(v)) ? Number(v) : NaN;
        };

        const normalizeData = (data) => {
            // ---- Force i18n language according to BDD defaultLang
            const defaultLang = data.defaultLang || 'fr';
            const supported = new Set(['fr', 'en', 'de']);
            const langToUse = supported.has(defaultLang) ? defaultLang : 'fr';

            // ✅ only change if necessary – avoids useless re-renders
            if (i18n.language !== langToUse) {
                i18n.changeLanguage(langToUse);
            }

            const groups = (data.groups || []).map((g, gi) => {
                const groupId = g.key || `grp_${gi}`;
                return {
                    id: groupId,
                    title: g.name?.[defaultLang] || g.name?.fr || g.name?.en || t('businessProposal.groups.fallbackTitle', { index: gi + 1 }),
                    type: g.selectionMode === 'single' ? 'radio' : 'checkbox',
                    required: !!g.required,
                    options: (g.options || []).map((opt, oi) => {
                        const title = resolveTitle(opt, defaultLang, `Option ${oi + 1}`);

                        const minQuantity = Number.isFinite(Number(opt.overrideMinQuantity))
                            ? Number(opt.overrideMinQuantity)
                            : 1;

                        const maxQuantity = Number.isFinite(Number(opt.overrideMaxQuantity))
                            ? Number(opt.overrideMaxQuantity)
                            : null;

                        const defaultQuantityRaw = opt.defaultQuantity;
                        const defaultQuantity = Number.isFinite(Number(defaultQuantityRaw))
                            ? Number(defaultQuantityRaw)
                            : minQuantity;

                        return {
                            id: opt._id || `opt_${groupId}_${oi}`,
                            title,
                            price: resolvePrice(opt),
                            publicPrice: resolvePublic(opt),
                            vat: resolveVAT(opt),
                            note: resolveNote(opt, defaultLang),
                            minQuantity,
                            maxQuantity,
                            defaultQuantity,
                            defaultSelected: !!opt.defaultSelected,
                        };
                    }),
                };
            });

            const services = (data.services || []).map((s, si) => {
                const title = resolveTitle(s, defaultLang, `Prestation ${si + 1}`);

                const minQuantity = Number.isFinite(Number(s.overrideMinQuantity))
                    ? Number(s.overrideMinQuantity)
                    : 1;

                const maxQuantity = Number.isFinite(Number(s.overrideMaxQuantity))
                    ? Number(s.overrideMaxQuantity)
                    : null;

                const defaultQuantityRaw = s.defaultQuantity;
                const defaultQuantity = Number.isFinite(Number(defaultQuantityRaw))
                    ? Number(defaultQuantityRaw)
                    : minQuantity;

                return {
                    id: s._id || `svc_${si}`,
                    title,
                    price: resolvePrice(s),
                    publicPrice: resolvePublic(s),
                    vat: resolveVAT(s),
                    note: resolveNote(s, defaultLang),
                    minQuantity,
                    maxQuantity,
                    defaultQuantity,
                    defaultSelected: !!s.defaultSelected,
                };
            });

            const allowedAccos = Array.isArray(data.allowedAccommodations)
                ? data.allowedAccommodations
                : ['none'];

            const sd = data.stayDates || null;
            const lockedStayDates = (sd && sd.startDate && sd.endDate)
                ? { startDate: new Date(sd.startDate), endDate: new Date(sd.endDate) }
                : null;

            setProposal({
                id: data._id,
                title: data.title?.[defaultLang] || data.title?.fr || data.title?.en || t('businessProposal.common.defaultTitle'),
                description: data.description?.[defaultLang] || data.description?.fr || data.description?.en || '',
                allowedAccommodations: allowedAccos,
                groups,
                priceDisplayMode: data.priceDisplayMode,
                services,
                lockedStayDates,
                customer: data.customer || null,
                billingAddress: data.billingAddress || null,
                societyInfos: data.societyInfos || null,
                customerUserId: data.customerUserId || null,
                status: data.status || null,
                validTo: data.validTo || null,
                quotationId: data.quotationId || null,
            });

            const initialQuantities = {};
            services.forEach((s) => {
                initialQuantities[`svc:${s.id}`] =
                    s.defaultQuantity ?? s.minQuantity ?? 1;
            });
            groups.forEach((g) => {
                g.options.forEach((o) => {
                    initialQuantities[`opt:${o.id}`] =
                        o.defaultQuantity ?? o.minQuantity ?? 1;
                });
            });

            setQuantities(initialQuantities);

            if (allowedAccos.length && !allowedAccos.includes('none')) {
                setAccommodation((prev) => prev || allowedAccos[0]);
            } else {
                setAccommodation('');
            }

            if (lockedStayDates) {
                setStayDates({
                    startDate: lockedStayDates.startDate,
                    endDate: lockedStayDates.endDate,
                });
            }
        };

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await Api.getBusinessProposal(id);
                const res = response?.data;
                if (!mounted) return;

                if (res?.status) {
                    normalizeData(res.data);
                } else {
                    // évite d'utiliser t ici pour ne pas le mettre dans les deps
                    setError(res?.error || t('businessProposal.common.notFound'));
                }
            } catch (e) {
                if (mounted) {
                    setError(e?.message || t('businessProposal.common.loadError'));
                }
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [id, i18n, t]);

    // ---------- Blocked dates / cheaper / promo ----------
    const fetchWithRetry = useCallback(async (apiCall, setter, defaultValue, tries = 3) => {
        for (let i = 0; i < tries; i++) {
            try {
                const resp = await apiCall();
                const ok = _.get(resp, 'data.status', false);
                if (ok) {
                    setter(_.get(resp, 'data.data', defaultValue));
                    return;
                }
            } catch (e) {
                // continue
            }
            await new Promise((r) => setTimeout(r, 700));
        }
        setter(defaultValue);
    }, []);

    useEffect(() => {
        (async () => {
            const stored = Api.getBlockedDatesStored?.() || [];
            if (stored?.length) setBlockedDates(stored);

            await fetchWithRetry(
                Api.getBlockedDates,
                (data) => {
                    setBlockedDates(data || []);
                    Api.storeBlockedDates?.(data || []);
                },
                []
            );
        })();
    }, [fetchWithRetry]);

    const toIsoDay = (d) => new Date(d).toISOString().slice(0, 10);

    const changeQuantity = (kind, id, delta, min, max) => {
        const key = `${kind}:${id}`;

        setQuantities((prev) => {
            const currentRaw = prev[key];
            const base = Number.isFinite(Number(currentRaw))
                ? Number(currentRaw)
                : (min ?? 1);

            let next = base + delta;

            if (min != null) {
                next = Math.max(next, min);
            }
            if (max != null && max > 0) {
                next = Math.min(next, max);
            }

            return {
                ...prev,
                [key]: next,
            };
        });
    };

    const handleQuantityButtonClick = (event, { kind, id, delta, qty, minQ, maxQ }) => {
        event.stopPropagation();

        // min reached
        if (delta < 0 && qty <= minQ) {
            Helper.toast(t('businessProposal.quantity.minReached'), "info");
            return;
        }

        // max reached (only if maxQ is defined)
        if (delta > 0 && maxQ != null && maxQ > 0 && qty >= maxQ) {
            Helper.toast(t('businessProposal.quantity.maxReached'), "info");
            return;
        }

        // OK, we can change quantity
        changeQuantity(kind, id, delta, minQ, maxQ);
    };

    const getNbMaxPeople = () => {
        if (!accommodation || accommodation === 'domain') {
            return MAX_PEOPLE_DOMAIN;
        }
        if (accommodation === 'home') {
            return MAX_PEOPLE_HOME;
        }
        if (accommodation === 'annex') {
            return MAX_PEOPLE_ANNEX;
        }
        return MAX_PEOPLE_DOMAIN;
    };

    const numberOptions = (labelKey, maxNumber = 22) => {
        return Array.from({ length: maxNumber + 1 }, (_, index) => {
            if (labelKey === "adult" && index === 0) {
                return null; // no "0 adult" option
            }
            return {
                value: `${index}`,
                label: `${index} ${t(`formInputs.${labelKey}`)}${index > 1 ? 's' : ''}`,
            };
        }).filter(Boolean);
    };

    const handleSelectChangeAdults = (event) => {
        const selectedAdults = Number(event.target.value);
        const totalPersons = selectedAdults + nbChilds;
        const maxPeople = getNbMaxPeople();

        if (totalPersons > maxPeople) {
            setNbChilds(maxPeople - selectedAdults);
        }
        setNbAdults(selectedAdults);
    };

    const handleSelectChangeChilds = (event) => {
        const selectedChildren = Number(event.target.value);
        const totalPersons = nbAdults + selectedChildren;
        const maxPeople = getNbMaxPeople();

        if (totalPersons > maxPeople) {
            setNbAdults(maxPeople - selectedChildren);
        }
        setNbChilds(selectedChildren);
    };

    const loadCheaperAndPromoDates = useCallback(
        async (accoKey) => {
            if (!accoKey) return;

            // baseline
            let base = Number(_.get(prices, [accoKey, 'base']) ?? _.get(prices, [accoKey, 'price']) ?? 0);
            if (!Number.isFinite(base) || base <= 0) {
                try {
                    const rackResp = await Api.getPrices();
                    const rack = Number(_.get(rackResp, ['data', 'data', accoKey, 'price']));
                    if (Number.isFinite(rack) && rack > 0) base = rack;
                } catch { }
            }

            try {
                const from = toIsoDay(new Date());
                const to = toIsoDay(new Date(new Date().setFullYear(new Date().getFullYear() + 2)));
                const resp = await Api.getDayOverrides(from, to, accoKey);
                const list = _.get(resp, 'data.data', []);

                const fmtD = (d) => (typeof d === 'string' ? d.slice(0, 10) : toIsoDay(d));

                const cheaper = Number.isFinite(base) && base > 0
                    ? list.filter((it) => !it.isPromotion && Number(it.price) < base).map((it) => fmtD(it.date))
                    : [];

                const promos = list.filter((it) => !!it.isPromotion).map((it) => fmtD(it.date));

                setCheaperDates(cheaper);
                setPromoDates(promos);
            } catch {
                setCheaperDates([]);
                setPromoDates([]);
            }
        },
        [prices]
    );

    useEffect(() => {
        if (accommodation) loadCheaperAndPromoDates(accommodation);
    }, [accommodation, loadCheaperAndPromoDates]);

    // ---------- Pricing for date range ----------
    const datesLocked = !!(proposal?.lockedStayDates?.startDate && proposal?.lockedStayDates?.endDate);

    const getNbDays = useCallback(() => {
        const s = stayDates?.startDate,
            e = stayDates?.endDate;
        if (!s || !e) return 0;
        const days = moment(e).diff(s, 'days');
        return Number.isFinite(days) ? days : 0;
    }, [stayDates]);

    const getPricesForAllowed = useCallback(async () => {
        if (!proposal) return;
        const s = stayDates?.startDate,
            e = stayDates?.endDate;
        if (!s || !e) return;

        const days = getNbDays();
        if (!days || days <= 0) return;

        const accs = proposal.allowedAccommodations?.filter((a) => a !== 'none') || [];
        if (!accs.length) return;

        const token = Symbol('bp-date-prices');
        lastPriceReqRef.current = token;

        const results = await Promise.all(
            accs.map(async (acc) => {
                try {
                    const resp = await Api.getPricesForDates(s, e, acc);
                    if (_.get(resp, 'data.status')) {
                        const data = _.get(resp, 'data.data', {});
                        const total = Number(data.accommodationPrice || 0);
                        const daily = days ? total / days : 0;
                        return {
                            acc,
                            price: Number(daily.toFixed(2)), // daily price for selected period
                            base: Number((data.basePrice ?? 0).toFixed(2)),
                            cleaning: Number(data.cleaning || 0),
                            promotion: Number(data.discountLongAccommodation || data.promotionDiscount || 0),
                        };
                    }
                } catch { }
                return { acc, price: 0, base: 0, cleaning: 0, promotion: 0 };
            })
        );

        if (lastPriceReqRef.current !== token) return; // stale

        setPrices((prev) => {
            const next = { ...prev };
            results.forEach((r) => {
                next[r.acc] = { ...next[r.acc], ...r };
            });
            return next;
        });
    }, [proposal, stayDates, getNbDays]);

    const normDisplayMode = (m) => {
        // NEW admin values
        switch (m) {
            case 'ht':
                return 'HT';
            case 'ttc':
                return 'TTC';
            case 'both_ht_first':
                return 'BOTH_HT_FIRST';
            case 'both_ttc_first':
                return 'BOTH_TTC_FIRST';
            case 'hide':
                return 'HIDE';
            default:
                break;
        }

        // Backward compatibility with old values if any
        if (m === 'HT' || m === 'HT_TTC' || m === 'PUBLIC_TTC' || m === 'PUBLIC_HT' || m === 'TTC') {
            return m;
        }

        return 'TTC';
    };

    // rack/base prices when no dates
    const loadRackIfNoDates = useCallback(async () => {
        if (!proposal) return;
        const s = stayDates?.startDate,
            e = stayDates?.endDate;
        if (s || e) return;

        try {
            const resp = await Api.getPrices();
            const ok = _.get(resp, 'data.status', false);
            if (!ok) return;

            const data = _.get(resp, 'data.data', {});
            setPrices((prev) => ({
                ...prev,
                domain: {
                    ...prev.domain,
                    price: Number(_.get(data, ['domain', 'price']) || 0),
                    base: Number(_.get(data, ['domain', 'price']) || 0),
                    cleaning: Number(_.get(data, ['domain', 'cleaning']) || 0),
                    promotion: 0,
                },
                home: {
                    ...prev.home,
                    price: Number(_.get(data, ['home', 'price']) || 0),
                    base: Number(_.get(data, ['home', 'price']) || 0),
                    cleaning: Number(_.get(data, ['home', 'cleaning']) || 0),
                    promotion: 0,
                },
                annex: {
                    ...prev.annex,
                    price: Number(_.get(data, ['annex', 'price']) || 0),
                    base: Number(_.get(data, ['annex', 'price']) || 0),
                    cleaning: Number(_.get(data, ['annex', 'cleaning']) || 0),
                    promotion: 0,
                },
            }));
        } catch { }
    }, [proposal, stayDates]);

    useEffect(() => {
        loadRackIfNoDates();
    }, [loadRackIfNoDates]);

    useEffect(() => {
        getPricesForAllowed();
    }, [getPricesForAllowed]);

    useEffect(() => {
        if (!proposal?.description) {
            setIsDescriptionClamped(false);
            setIsDescriptionExpanded(false);
            return;
        }

        // On laisse un petit timeout pour que le layout soit calculé
        const timer = setTimeout(() => {
            if (!descriptionRef.current) return;
            const el = descriptionRef.current;

            // Si le contenu déborde, on marque comme "clampé"
            const isOverflowing = el.scrollHeight > el.clientHeight + 1;
            setIsDescriptionClamped(isOverflowing);
        }, 0);

        return () => clearTimeout(timer);
    }, [proposal?.description]);

    // ---------- Toggles ----------
    const toggleService = (id) => {
        setSelectedServices((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleGroup = (groupId, type, optionId) => {
        setGroupSelections((prev) => {
            const next = { ...prev };
            if (type === 'radio') {
                next[groupId] = optionId;
            } else {
                const set = next[groupId] instanceof Set ? new Set(next[groupId]) : new Set();
                set.has(optionId) ? set.delete(optionId) : set.add(optionId);
                next[groupId] = set;
            }
            return next;
        });
    };

    const clearAll = () => {
        setSelectedServices(new Set());
        setGroupSelections({});
        setAccommodation('');
        // reset to locked dates if any, else clear
        if (datesLocked) {
            setStayDates({
                startDate: new Date(proposal.lockedStayDates.startDate),
                endDate: new Date(proposal.lockedStayDates.endDate),
            });
        } else {
            setStayDates({ startDate: null, endDate: null });
        }
    };

    // ---------- Selected items + totals ----------
    const selectedItems = useMemo(() => {
        if (!proposal) return [];
        const items = [];

        // Services
        proposal.services?.forEach((s) => {
            if (!selectedServices.has(s.id)) return;

            const key = `svc:${s.id}`;
            const rawQty = quantities[key];

            let qty =
                Number.isFinite(Number(rawQty)) && Number(rawQty) > 0
                    ? Number(rawQty)
                    : (Number.isFinite(Number(s.defaultQuantity)) && Number(s.defaultQuantity) > 0
                        ? Number(s.defaultQuantity)
                        : (Number.isFinite(Number(s.minQuantity)) && Number(s.minQuantity) > 0
                            ? Number(s.minQuantity)
                            : 1
                        )
                    );

            items.push({
                ...s,
                groupId: null,
                kind: 'service',
                quantity: qty,
            });
        });

        // Groups
        proposal.groups?.forEach((g) => {
            if (g.type === 'radio') {
                const selectedId = groupSelections[g.id];
                const opt = g.options.find((o) => o.id === selectedId);
                if (!opt) return;

                const key = `opt:${opt.id}`;
                const rawQty = quantities[key];

                let qty =
                    Number.isFinite(Number(rawQty)) && Number(rawQty) > 0
                        ? Number(rawQty)
                        : (Number.isFinite(Number(opt.defaultQuantity)) && Number(opt.defaultQuantity) > 0
                            ? Number(opt.defaultQuantity)
                            : (Number.isFinite(Number(opt.minQuantity)) && Number(opt.minQuantity) > 0
                                ? Number(opt.minQuantity)
                                : 1
                            )
                        );

                items.push({
                    ...opt,
                    groupId: g.id,
                    kind: 'group',
                    quantity: qty,
                });

            } else {
                const set = groupSelections[g.id];
                if (!(set instanceof Set)) return;

                g.options.forEach((o) => {
                    if (!set.has(o.id)) return;

                    const key = `opt:${o.id}`;
                    const rawQty = quantities[key];

                    let qty =
                        Number.isFinite(Number(rawQty)) && Number(rawQty) > 0
                            ? Number(rawQty)
                            : (Number.isFinite(Number(o.defaultQuantity)) && Number(o.defaultQuantity) > 0
                                ? Number(o.defaultQuantity)
                                : (Number.isFinite(Number(o.minQuantity)) && Number(o.minQuantity) > 0
                                    ? Number(o.minQuantity)
                                    : 1
                                )
                            );

                    items.push({
                        ...o,
                        groupId: g.id,
                        kind: 'group',
                        quantity: qty,
                    });
                });
            }
        });

        return items;
    }, [proposal, selectedServices, groupSelections, quantities]);

    const datesReady = useMemo(() => !!(stayDates?.startDate && stayDates?.endDate), [stayDates]);

    const nbDays = useMemo(() => {
        const s = stayDates?.startDate,
            e = stayDates?.endDate;
        if (!s || !e) return 0;
        const days = moment(e).diff(s, 'days');
        return Number.isFinite(days) ? days : 0;
    }, [stayDates]);

    // tourist tax (same logic as your Reservation page; fixed 2 adults default here)
    const touristTaxForRange = useCallback(
        (startDate, endDate) => {
            const adults = nbAdults || 0;
            if (!startDate || !endDate || !adults) return 0;

            const days = moment(endDate).diff(startDate, 'days');
            if (!Number.isFinite(days) || days <= 0) return 0;

            const start = new Date(startDate);
            const threshold = new Date('2026-01-01');
            const rate = start >= threshold ? 3.63 : 3.3;

            return Number((adults * rate * days).toFixed(2));
        },
        [nbAdults]
    );

    // Total accommodation for selected dates (if any)
    const accommodationTotal = useMemo(() => {
        if (!accommodation || !datesReady) return 0;
        const p = prices[accommodation] || {};
        const pricePerNight = Number(p.price || 0);
        const cleaning = Number(p.cleaning || 0);
        const promo = Number(p.promotion || 0); // negative or zero
        const tTax = touristTaxForRange(stayDates?.startDate, stayDates?.endDate);

        if (nbDays > 0 && pricePerNight > 0) {
            return Number((nbDays * pricePerNight + cleaning + tTax - promo).toFixed(2));
        }
        return 0;
    }, [accommodation, prices, datesReady, nbDays, stayDates, touristTaxForRange]);

    // Services total using price display mode (TTC/HT...)
    const priceMode = normDisplayMode(proposal?.priceDisplayMode);

    const servicesTotal = useMemo(() => {
        if (!selectedItems.length) return 0;

        return selectedItems.reduce((sum, s) => {
            const { TTC } = deriveHTTTC({
                price: s.price,
                publicPrice: s.publicPrice,
                vat: s.vat,
                mode: priceMode,
                t
            });

            const qty = Number.isFinite(Number(s.quantity))
                ? Number(s.quantity)
                : 1;

            return sum + (Number.isFinite(TTC) ? TTC * qty : 0);
        }, 0);
    }, [selectedItems, priceMode, t]);

    // Only include accommodation when dates are picked
    const grandTotal = useMemo(
        () => servicesTotal + (datesReady ? accommodationTotal : 0),
        [servicesTotal, accommodationTotal, datesReady]
    );

    const now = new Date();
    const isExpired = proposal?.validTo ? new Date(proposal.validTo) < now : false;
    const isAccepted = proposal?.status === 'accepted';
    const canAccept = !isExpired && !isAccepted;

    const quotationId = savedQuotation?._id || proposal?.quotationId || null;

    const validateRequiredGroups = () => {
        if (!proposal?.groups?.length) return true;

        const missing = proposal.groups.filter((g) => {
            if (!g.required) return false;

            const sel = groupSelections[g.id];

            if (g.type === 'radio') {
                return !sel; // Nothing chosen
            }

            if (sel instanceof Set) {
                return sel.size === 0; // Empty set
            }

            return true;
        });

        if (missing.length) {
            Helper.toast(
                t('businessProposal.groups.requiredError', {
                    count: missing.length,
                }),
                'error'
            );

            // --- SCROLL LOGIC START ---
            const firstId = missing[0].id;

            // We use setTimeout to push the scroll to the end of the call stack
            // ensuring the DOM is stable and ready to scroll.
            setTimeout(() => {
                const el = groupRefs.current[firstId];

                if (el) {
                    // 1. Get the distance from the top of the viewport
                    const elementPosition = el.getBoundingClientRect().top;

                    // 2. Get the current scroll position
                    const offsetPosition = elementPosition + window.pageYOffset;

                    // 3. Subtract header height + extra buffer
                    const headerOffset = 200;

                    window.scrollTo({
                        top: offsetPosition - headerOffset,
                        behavior: 'smooth'
                    });
                } else {
                    console.warn("Scroll target not found for ID:", firstId);
                }
            }, 100);
            // --- SCROLL LOGIC END ---

            return false;
        }

        return true;
    };

    const handleSaveClick = async () => {
        // hard guard: ignore extra clicks while saving
        if (isSavingRef.current) return;

        isSavingRef.current = true;
        setIsSaving(true);
        try {
            await saveSelectionAsQuotation();
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    };

    const saveSelectionAsQuotation = async () => {
        try {
            if (!proposal) return;

            if (isExpired) {
                return Helper.toast(
                    t('businessProposal.errors.expiredMessage'),
                    "error"
                );
            }

            if (isAccepted) {
                return Helper.toast(
                    t('businessProposal.summary.alreadyAcceptedMessage'),
                    "info"
                );
            }

            if (!validateRequiredGroups()) {
                return; // on bloque l'envoi
            }

            // si on a un choix d'hébergement mais pas de dates -> on prévient
            const hasAccoChoiceLocal =
                Array.isArray(proposal.allowedAccommodations) &&
                proposal.allowedAccommodations.length > 0 &&
                !proposal.allowedAccommodations.includes('none');

            if (hasAccoChoiceLocal && accommodation && !datesReady) {
                return Helper.toast(
                    t('businessProposal.errors.onlyWhenDates'),
                    "info"
                );
            }

            const nowLocal = new Date();
            const oneMonthLater = new Date(
                nowLocal.getFullYear(),
                nowLocal.getMonth() + 1,
                nowLocal.getDate()
            );

            const cleaning = Number(prices?.[accommodation]?.cleaning || 0);
            const discountLongAccommodation = Number(
                prices?.[accommodation]?.promotion || 0
            );
            const touristTax = touristTaxForRange(
                stayDates?.startDate,
                stayDates?.endDate
            );

            // prix hébergement hors ménage + taxe
            const rawAccommodationPrice = accommodationTotal
                ? accommodationTotal - cleaning - touristTax
                : 0;

            // données client / adresse depuis la proposition
            const customerFromProposal = proposal.customer || {};
            const billingAddress = proposal.billingAddress || {};
            const societyInfosFromProposal = proposal.societyInfos || {};

            const quotation = {
                // pour tracer l’origine
                dates: {
                    startDate: stayDates?.startDate || null,
                    endDate: stayDates?.endDate || null,
                    creationDate: nowLocal,
                    expirationDate: oneMonthLater,
                },

                mainInfos: {
                    status: 'sent',
                    language: i18n.language,
                    numberOfGuests: {
                        adults: nbAdults || null,
                        childs: nbChilds || null,
                    },
                    comment: proposal.title || undefined,
                },

                prices: {
                    accommodationPrice: Number(
                        Number(rawAccommodationPrice || 0).toFixed(2)
                    ),
                    typeOfAccommodation: accommodation || 'none',
                    totalPrice: Number(Number(grandTotal || 0).toFixed(2)),
                    cleaning,
                    discountLongAccommodation,
                    depositPercentage: 100,
                    touristTax,
                },

                // --- CLIENT ---
                customer: {
                    lastname: customerFromProposal.lastname,
                    firstname: customerFromProposal.firstname,
                    email: customerFromProposal.email,
                    phone: customerFromProposal.phone,
                    // si la société est connue, on la met ici
                    nameSociety: societyInfosFromProposal.name,
                    numTVA: customerFromProposal.numTVA,
                    siret: customerFromProposal.siret,
                },

                // --- ADRESSE DE FACTURATION ---
                address: {
                    address: billingAddress.address,
                    address2: billingAddress.address2,
                    postalCode: billingAddress.postalCode,
                    city: billingAddress.city,
                    country: billingAddress.country,
                },

                guests: [],

                services: selectedItems.map((item) => {
                    const { TTC } = deriveHTTTC({
                        price: item.price,
                        publicPrice: item.publicPrice,
                        vat: item.vat,
                        mode: priceMode,
                        t
                    });

                    const qty = Number.isFinite(Number(item.quantity))
                        ? Number(item.quantity)
                        : 1;

                    return {
                        slug: String(item.id),
                        name: item.title,
                        quantity: qty,
                        isPriceDefined: true,
                        price: Number(Number(TTC || 0).toFixed(2)),        // prix unitaire TTC
                        publicPrice: Number(Number(TTC || 0).toFixed(2)),  // idem
                        VAT: item.vat,
                        priceMustBePaidEach: 'visit',
                        isInternal: false,
                    };
                }),

                history: [
                    {
                        key: 'businessProposalId',
                        value: String(proposal.id),
                    },
                    {
                        key: 'businessProposalSelection',
                        value: {
                            date: nowLocal,
                            accommodation,
                            stayDates,
                            groupSelections: serializeGroupSelections(
                                groupSelections
                            ),
                            selectedServices: Array.from(selectedServices),
                            grandTotal: Number(
                                Number(grandTotal || 0).toFixed(2)
                            ),
                        },
                    },
                ],
            };

            const response = await Api.acceptBusinessProposal(proposal.id, quotation);

            if (response?.data?.status) {
                const resData = response.data.data;

                if (resData?.proposal) {
                    setProposal((prev) =>
                        prev
                            ? {
                                ...prev,
                                status: resData.proposal.status || 'accepted',
                                validTo: resData.proposal.validTo || prev.validTo,
                            }
                            : prev
                    );
                } else {
                    setProposal((prev) =>
                        prev ? { ...prev, status: 'accepted' } : prev
                    );
                }

                if (resData?.quotation) {
                    setSavedQuotation(resData.quotation);
                }

                Helper.toast(t('responses.quotationSend'), "success");
                setAfterSaveView(true);

            } else {
                console.error('saveQuotation failed', response);
                return Helper.toast(t('responses.quotationError'), "error");
            }

        } catch (e) {
            console.error('saveSelectionAsQuotation error', e);
            return Helper.toast(t('responses.quotationError'), "error");
        }
    };


    if (loading) return <Loading />;

    if (error || !proposal) {
        return (
            <>
                <header className="flex justify-center pt-8">
                    <LogoLong width={250} height={64} />
                </header>

                <div className="m-auto max-w-5xl pt-14">
                    <div className="px-4 md:px-0">
                        <div className="text-center">
                            <H2 title={t('businessProposal.pageError.title')} />

                            <div className="mt-5">
                                <DescriptionSplit description={t('businessProposal.pageError.description')} />
                            </div>

                            <div className="mt-5">
                                <Link link="/" disableHoverEffect={true}>
                                    <Button text={t('businessProposal.pageError.button')} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }


    const hasAccoChoice =
        Array.isArray(proposal.allowedAccommodations) &&
        proposal.allowedAccommodations.length > 0 &&
        !proposal.allowedAccommodations.includes('none');

    const displayedAccommodation = hasAccoChoice
        ? proposal.allowedAccommodations.filter((a) => a !== 'none')
        : [];

    const handleDateChange = (e) => {
        if (datesLocked) return; // ignore changes if locked by admin
        setStayDates(e.date); // { startDate, endDate }
    };

    if (afterSaveView) {
        const isExpiredLocal = proposal.validTo ? new Date(proposal.validTo) < now : false;
        const isAcceptedLocal = proposal.status === 'accepted';

        // En théorie, ici on est dans un cas "accepté", mais on garde les safeties.
        if (isExpiredLocal && !isAcceptedLocal) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="max-w-md w-full bg-white shadow p-6 text-center">
                        <h1 className="text-xl font-semibold mb-2">
                            {t('businessProposal.errors.expiredTitle')}
                        </h1>
                        <p className="text-sm text-gray-600 mb-4">
                            {t('businessProposal.errors.expiredMessage')}
                        </p>
                        <p className="text-xs text-gray-400">
                            {t('businessProposal.errors.expiredCode')}
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div className="min-h-screen bg-gray-50">
                {/* Header conservant le style global */}
                <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
                    <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-start md:items-center flex-col md:flex-row gap-3 md:gap-0">
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                                {proposal.title}
                            </h1>

                            {proposal.description && (
                                <div className="mt-0.5">
                                    <p
                                        ref={descriptionRef}
                                        className={`
                    text-gray-500 
                    text-xs sm:text-sm 
                    flex items-center gap-2
                    ${!isDescriptionExpanded ? 'overflow-hidden' : ''}
                `}
                                        style={
                                            !isDescriptionExpanded
                                                ? {
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                }
                                                : {}
                                        }
                                    >
                                        {proposal.description}
                                    </p>

                                    {isDescriptionClamped && (
                                        <button
                                            type="button"
                                            className="mt-1 text-xs text-gray-600 underline"
                                            onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                                        >
                                            {isDescriptionExpanded
                                                ? t('businessProposal.common.showLess')
                                                : t('businessProposal.common.showMore')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main className="max-w-6xl mx-auto px-4 py-4 md:py-10">
                    <div className="max-w-3xl mx-auto border border-gray-200 bg-white p-6 md:p-8 shadow-sm space-y-5">
                        <p className="text-sm text-gray-700">
                            {t('businessProposal.summary.acceptedExplanation')}
                        </p>
                        <p className="text-sm text-gray-700">
                            {t('businessProposal.summary.acceptedEmailInfo')}
                        </p>

                        <div className="border p-4 text-sm space-y-2 bg-gray-50">
                            <div className="flex justify-between">
                                <span className="font-medium">
                                    {t('businessProposal.summary.proposalTitle')}
                                </span>
                                <span>{proposal.title}</span>
                            </div>
                            {proposal.validTo && (
                                <div className="flex justify-between text-gray-600">
                                    <span>{t('businessProposal.summary.validTo')}</span>
                                    <span>{new Date(proposal.validTo).toLocaleDateString()}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="font-medium">
                                    {t('businessProposal.common.currencyTotal')}
                                </span>
                                <span className="font-semibold">
                                    {euro(grandTotal)}{' '}
                                    <span className="text-xs font-normal text-gray-500 uppercase">
                                        {t('businessProposal.prices.ttc_without_value')}
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-3">
                            <button
                                type="button"
                                onClick={() => navigate('/')}
                                className="w-full inline-flex justify-center border border-gray-300 px-4 py-3 text-sm hover:bg-gray-100"
                            >
                                {t('businessProposal.summary.backToHome')}
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    if (savedQuotation?._id) {
                                        navigate(`/quotation/${savedQuotation._id}`);
                                    }
                                }}
                                disabled={!savedQuotation?._id}
                                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium  
                                    ${savedQuotation?._id ? 'bg-main text-white hover:bg-main-dark' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
                                `}
                            >
                                {t('businessProposal.summary.goToQuotation')}
                            </button>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
                {isExpired && (
                    <div className="w-full bg-red-50 border-b border-red-200">
                        <div className="max-w-6xl mx-auto px-2.5 py-3 text-sm text-red-800 font-medium flex items-center gap-2">
                            <i className="fas fa-exclamation-triangle" />
                            {t('businessProposal.errors.expiredHeader')}
                        </div>
                    </div>
                )}

                <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-start md:items-center flex-col md:flex-row gap-3 md:gap-0">
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                            {proposal.title}
                        </h1>

                        {proposal.description && (
                            <div className="mt-0.5">
                                <p
                                    ref={descriptionRef}
                                    className={`
                    text-gray-500 
                    text-xs sm:text-sm 
                    flex items-center gap-2
                    ${!isDescriptionExpanded ? 'overflow-hidden' : ''}
                `}
                                    style={
                                        !isDescriptionExpanded
                                            ? {
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                            }
                                            : {}
                                    }
                                >
                                    {proposal.description}
                                </p>

                                {isDescriptionClamped && (
                                    <button
                                        type="button"
                                        className="mt-1 text-xs text-gray-600 underline"
                                        onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                                    >
                                        {isDescriptionExpanded
                                            ? t('businessProposal.common.showLess')
                                            : t('businessProposal.common.showMore')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={clearAll}
                            className="flex w-max items-center border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100"
                            title={t('businessProposal.common.clearAll')}
                        >
                            <span className="whitespace-normal break-words text-left">
                                {t('businessProposal.common.clearAll')}
                            </span>
                        </button>
                        <div className="md:flex items-center gap-2 bg-main text-white px-4 py-2 text-sm h-fit">
                            {t('businessProposal.common.currencyTotal')}&nbsp;:&nbsp;{euro(grandTotal)}
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-4 md:py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Left content */}
                <div className="lg:col-span-8 space-y-10">
                    {/* Hébergement + Dates */}
                    {hasAccoChoice && (
                        <Section
                            title={t('businessProposal.accommodation.sectionTitle')}
                            subtitle={t('businessProposal.accommodation.sectionSubtitle')}
                        >
                            {/* Dates + guests (always same UI, just disabled if locked) */}
                            <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-sm font-medium text-gray-700">
                                        {t('businessProposal.accommodation.stayPeriod')}
                                    </span>
                                    {datesLocked && (
                                        <span className="text-xs text-gray-500">
                                            {t('businessProposal.common.adminDefined')}
                                        </span>
                                    )}
                                </div>

                                <div className="flex w-full flex-col">
                                    {/* Date picker – same style as Reservation, just disabled when locked */}
                                    <div className="flex w-full">
                                        <DateFieldInput
                                            label="stayDate"
                                            value={stayDates}
                                            minDate={new Date()}
                                            maxDate={twoYearsLater}
                                            onChange={handleDateChange}
                                            displayLabel={false}
                                            singleDate={false}
                                            blockedDates={blockedDates}
                                            highlightDates={cheaperDates}
                                            promoDates={promoDates}
                                            className="rounded-none"
                                            disabled={datesLocked}
                                        />
                                    </div>

                                    {/* Adults / childs selectors */}
                                    <div className="w-full block md:flex">
                                        <Select
                                            label="adults"
                                            name={t('formInputs.adult')}
                                            options={numberOptions("adult", getNbMaxPeople() - nbChilds)}
                                            value={nbAdults}
                                            onChange={handleSelectChangeAdults}
                                            displayLabel={false}
                                            iconDisplayed={faUser}
                                            error={null}
                                            placeholder={'reservation.details.nbAdults'}
                                            className="rounded-none"
                                        />
                                        <Select
                                            label="childs"
                                            name={t('formInputs.child')}
                                            options={numberOptions("child", getNbMaxPeople() - nbAdults)}
                                            value={nbChilds}
                                            onChange={handleSelectChangeChilds}
                                            displayLabel={false}
                                            iconDisplayed={faUser}
                                            error={null}
                                            placeholder={'reservation.details.nbChildsWithAge'}
                                            className="rounded-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Accommodation cards */}
                            <div className="my-4 flex flex-col justify-between gap-4">
                                <AccommodationDisplayed
                                    accommodation={displayedAccommodation}
                                    accommodationSelected={accommodation}
                                    prices={prices}
                                    totalPeople={nbAdults + nbChilds}
                                    onChange={(val) => {
                                        setAccommodation(val);
                                        loadCheaperAndPromoDates(val);
                                    }}
                                    nameResolver={longAccommodationName}
                                />
                            </div>

                            <div className="mt-1">
                                {accommodation ? (
                                    <span className="text-sm text-gray-600">
                                        {t('businessProposal.accommodation.selectedAccommodation')}:&nbsp;
                                        <b>{longAccommodationName(accommodation)}</b>
                                        {nbDays > 0 && (
                                            <span>
                                                &nbsp;
                                                {t('businessProposal.accommodation.nbNights', {
                                                    count: nbDays,
                                                })}
                                            </span>
                                        )}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-500">
                                        {t('businessProposal.accommodation.noAccommodationSelected')}
                                    </span>
                                )}
                            </div>
                        </Section>
                    )}

                    {/* Groups */}
                    {proposal.groups?.map((group) => (
                        <Section
                            key={group.id}
                            title={group.title}
                            subtitle={
                                group.type === 'radio'
                                    ? t('businessProposal.groups.radioSubtitle')
                                    : t('businessProposal.groups.checkboxSubtitle')
                            }
                            data-group-id={group.id}
                            ref={(el) => {
                                if (el) {
                                    groupRefs.current[group.id] = el;
                                }
                            }}
                        >
                            <div className="grid md:grid-cols-2 gap-4">
                                {group.options.map((opt) => {
                                    const active =
                                        group.type === 'radio'
                                            ? groupSelections[group.id] === opt.id
                                            : groupSelections[group.id] instanceof Set &&
                                            groupSelections[group.id].has(opt.id);

                                    const priceInfo = deriveHTTTC({
                                        price: opt.price,
                                        publicPrice: opt.publicPrice,
                                        vat: opt.vat,
                                        mode: priceMode,
                                        t,
                                    });
                                    const { label, secondaryLabel } = priceInfo;

                                    // 🔽 QUANTITY CALC FOR GROUP OPTION
                                    const key = `opt:${opt.id}`;
                                    const rawQty = quantities[key];
                                    const minQ = Number.isFinite(Number(opt.minQuantity))
                                        ? Number(opt.minQuantity)
                                        : 1;
                                    const maxQ = Number.isFinite(Number(opt.maxQuantity))
                                        ? Number(opt.maxQuantity)
                                        : null;
                                    const qty =
                                        Number.isFinite(Number(rawQty)) && Number(rawQty) > 0
                                            ? Number(rawQty)
                                            : (Number.isFinite(Number(opt.defaultQuantity)) && Number(opt.defaultQuantity) > 0
                                                ? Number(opt.defaultQuantity)
                                                : minQ
                                            );

                                    // 👇 show quantity control only if more than 1 possible
                                    const canChangeQty = !(minQ === 1 && maxQ === 1);
                                    const isAtMin = qty <= minQ;
                                    const isAtMax = maxQ != null && maxQ > 0 && qty >= maxQ;

                                    return (
                                        <Card
                                            key={opt.id}
                                            active={active}
                                            onClick={() =>
                                                toggleGroup(group.id, group.type, opt.id)
                                            }
                                        >
                                            <div className="shrink-0 mt-0.5">
                                                <i
                                                    className={`fas ${group.type === 'radio'
                                                        ? active
                                                            ? 'fa-dot-circle text-gray-900'
                                                            : 'fa-circle text-gray-300'
                                                        : active
                                                            ? 'fa-check-square text-gray-900'
                                                            : 'fa-square text-gray-300'
                                                        } text-lg`}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-center gap-3">
                                                    <div className='flex items-center gap-2'>
                                                        {canChangeQty && (
                                                            <div className="inline-flex flex-col-reverse items-center gap-1 rounded-full bg-gray-50 border border-gray-200 px-1.5 py-0.5">
                                                                {/* minus */}
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) =>
                                                                        handleQuantityButtonClick(e, {
                                                                            kind: 'opt',
                                                                            id: opt.id,
                                                                            delta: -1,
                                                                            qty,
                                                                            minQ,
                                                                            maxQ,
                                                                        })
                                                                    }
                                                                    className={`h-6 w-6 flex items-center justify-center rounded-full text-xs
                                                                        ${isAtMin
                                                                            ? 'text-gray-300 cursor-not-allowed hover:bg-transparent'
                                                                            : 'text-gray-700 hover:bg-gray-200'
                                                                        }`}
                                                                >
                                                                    -
                                                                </button>

                                                                <span className="min-w-[1.75rem] text-center text-xs font-medium">
                                                                    {qty}x
                                                                </span>

                                                                {/* plus */}
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) =>
                                                                        handleQuantityButtonClick(e, {
                                                                            kind: 'opt',
                                                                            id: opt.id,
                                                                            delta: +1,
                                                                            qty,
                                                                            minQ,
                                                                            maxQ,
                                                                        })
                                                                    }
                                                                    className={`h-6 w-6 flex items-center justify-center rounded-full text-xs
                                                                        ${isAtMax
                                                                            ? 'text-gray-300 cursor-not-allowed hover:bg-transparent'
                                                                            : 'text-gray-700 hover:bg-gray-200'
                                                                        }`}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        )}
                                                        <h3 className="font-medium">{opt.title}</h3>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        {label && (
                                                            <PricePill primary={label} secondary={secondaryLabel} />
                                                        )}
                                                    </div>
                                                </div>
                                                {opt.note && (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {opt.note}
                                                    </p>
                                                )}
                                            </div>
                                        </Card>
                                    );
                                })}
                            </div>
                            {group.required && (
                                <p className="mt-2 text-xs text-gray-500">
                                    {t('businessProposal.groups.requiredHint')}
                                </p>
                            )}
                            {(group.type === 'radio' && !group.required) && (
                                <div className="mt-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setGroupSelections((prev) => ({
                                                ...prev,
                                                [group.id]: undefined,
                                            }))
                                        }
                                        className="text-sm text-gray-600 hover:text-gray-900 underline"
                                    >
                                        {t('businessProposal.groups.unselectRadio')}
                                    </button>
                                </div>
                            )}
                        </Section>
                    ))}

                    {/* Standalone Services */}
                    {proposal.services?.length > 0 && (
                        <Section
                            title={t('businessProposal.services.sectionTitle')}
                            subtitle={t('businessProposal.services.sectionSubtitle')}
                        >
                            <div className="grid md:grid-cols-2 gap-4">
                                {proposal.services.map((svc) => {
                                    const active = selectedServices.has(svc.id);
                                    const priceInfo = deriveHTTTC({
                                        price: svc.price,
                                        publicPrice: svc.publicPrice,
                                        vat: svc.vat,
                                        mode: priceMode,
                                        t,
                                    });
                                    const { label, secondaryLabel } = priceInfo;

                                    // 🔽 QUANTITY CALC FOR SERVICE
                                    const key = `svc:${svc.id}`;
                                    const rawQty = quantities[key];
                                    const minQ = Number.isFinite(Number(svc.minQuantity))
                                        ? Number(svc.minQuantity)
                                        : 1;
                                    const maxQ = Number.isFinite(Number(svc.maxQuantity))
                                        ? Number(svc.maxQuantity)
                                        : null;
                                    const qty =
                                        Number.isFinite(Number(rawQty)) && Number(rawQty) > 0
                                            ? Number(rawQty)
                                            : (Number.isFinite(Number(svc.defaultQuantity)) && Number(svc.defaultQuantity) > 0
                                                ? Number(svc.defaultQuantity)
                                                : minQ
                                            );

                                    // 👇 hide quantity selector if strictly 1
                                    const canChangeQty = !(minQ === 1 && maxQ === 1);
                                    const isAtMin = qty <= minQ;
                                    const isAtMax = maxQ != null && maxQ > 0 && qty >= maxQ;

                                    return (
                                        <Card
                                            key={svc.id}
                                            active={active}
                                            onClick={() => toggleService(svc.id)}
                                        >
                                            <div className="shrink-0 mt-0.5">
                                                <i
                                                    className={`fas ${active
                                                        ? 'fa-check-square text-gray-900'
                                                        : 'fa-square text-gray-300'
                                                        } text-lg`}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-center gap-3">
                                                    <div className='flex items-center gap-2'>
                                                        {canChangeQty && (
                                                            <div className="inline-flex flex-col-reverse items-center gap-1 rounded-full bg-gray-50 border border-gray-200 px-1.5 py-0.5">
                                                                {/* minus */}
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) =>
                                                                        handleQuantityButtonClick(e, {
                                                                            kind: 'svc',
                                                                            id: svc.id,
                                                                            delta: -1,
                                                                            qty,
                                                                            minQ,
                                                                            maxQ,
                                                                        })
                                                                    }
                                                                    className={`h-6 w-6 flex items-center justify-center rounded-full text-xs
                                                                        ${isAtMin
                                                                            ? 'text-gray-300 cursor-not-allowed hover:bg-transparent'
                                                                            : 'text-gray-700 hover:bg-gray-200'
                                                                        }`}
                                                                >
                                                                    −
                                                                </button>

                                                                <span className="min-w-[1.75rem] text-center text-xs font-medium">
                                                                    {qty}x
                                                                </span>

                                                                {/* plus */}
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) =>
                                                                        handleQuantityButtonClick(e, {
                                                                            kind: 'svc',
                                                                            id: svc.id,
                                                                            delta: +1,
                                                                            qty,
                                                                            minQ,
                                                                            maxQ,
                                                                        })
                                                                    }
                                                                    className={`h-6 w-6 flex items-center justify-center rounded-full text-xs
                                                                        ${isAtMax
                                                                            ? 'text-gray-300 cursor-not-allowed hover:bg-transparent'
                                                                            : 'text-gray-700 hover:bg-gray-200'
                                                                        }`}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        )}

                                                        <h3 className="font-medium">{svc.title}</h3>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <PricePill primary={label} secondary={secondaryLabel} />
                                                    </div>
                                                </div>
                                                {svc.note && (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {svc.note}
                                                    </p>
                                                )}
                                            </div>
                                        </Card>
                                    );
                                })}
                            </div>
                        </Section>
                    )}
                    <div className="mt-4 text-xs text-gray-500 leading-snug">
                        {t('businessProposal.common.priceDisclaimer')}
                    </div>
                </div>

                {/* Right Summary */}
                <aside className="lg:col-span-4">
                    <div className="border border-gray-200 bg-white p-5 shadow-sm sticky top-[92px]">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                {t('businessProposal.summary.title')}
                            </h3>
                            <button
                                onClick={clearAll}
                                className="text-sm text-gray-500 hover:text-gray-900 underline flex items-center gap-2"
                                title={t('businessProposal.common.reset')}
                            >
                                {t('businessProposal.common.reset')}
                            </button>
                        </div>

                        {/* Accommodation recap with detailed lines */}
                        {hasAccoChoice && (
                            <div className="mb-4">
                                <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                                    {t('businessProposal.accommodation.sectionTitle')}
                                </div>

                                {!accommodation ? (
                                    <p className="text-sm text-gray-500">
                                        {t('businessProposal.accommodation.noAccommodationSelected')}
                                    </p>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between py-2">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold uppercase tracking-wide">
                                                    {longAccommodationName(accommodation)}
                                                </span>
                                                {!datesReady ? (
                                                    <span className="text-xs text-gray-500">
                                                        {t(
                                                            'businessProposal.accommodation.details.selectDatesHint'
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-500">
                                                        {t('businessProposal.accommodation.perNightPrice')}{' '}
                                                        (
                                                        {t(
                                                            'businessProposal.accommodation.nbNights',
                                                            { count: nbDays }
                                                        )}
                                                        )
                                                    </span>
                                                )}
                                            </div>

                                            {/* Per-night price only when dates are set */}
                                            {datesReady && (
                                                <div className="text-sm font-medium whitespace-nowrap">
                                                    {euro(
                                                        prices?.[accommodation]?.price || 0
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Detail lines – only when dates are set */}
                                        {datesReady && (
                                            <>
                                                <div className="pl-2 mt-1 flex justify-between opacity-60">
                                                    <span className="text-sm">
                                                        {t(
                                                            'businessProposal.accommodation.details.linen'
                                                        )}
                                                    </span>
                                                    <span className="text-sm">
                                                        {t('businessProposal.common.included')}
                                                    </span>
                                                </div>

                                                <div className="pl-2 mt-1 flex justify-between opacity-60">
                                                    <span className="text-sm">
                                                        {t(
                                                            'businessProposal.accommodation.details.poolTrash'
                                                        )}
                                                    </span>
                                                    <span className="text-sm">
                                                        {t('businessProposal.common.included')}
                                                    </span>
                                                </div>

                                                <div className="mt-1 flex justify-between opacity-60">
                                                    <span className="text-sm">
                                                        {t(
                                                            'businessProposal.accommodation.details.endCleaning'
                                                        )}
                                                    </span>
                                                    <span className="text-sm">
                                                        {euro(
                                                            prices?.[accommodation]?.cleaning ||
                                                            0
                                                        )}
                                                    </span>
                                                </div>

                                                <div className="mt-1 flex justify-between opacity-60">
                                                    <span className="text-sm">
                                                        {t(
                                                            'businessProposal.accommodation.details.touristTax'
                                                        )}
                                                    </span>
                                                    <span className="text-sm">
                                                        {euro(
                                                            touristTaxForRange(
                                                                stayDates?.startDate,
                                                                stayDates?.endDate
                                                            )
                                                        )}
                                                    </span>
                                                </div>

                                                <div className="flex mt-4 justify-between">
                                                    <span className="text-base font-semibold">
                                                        {t(
                                                            'businessProposal.accommodation.details.subtotal'
                                                        )}
                                                    </span>
                                                    <span className="text-base font-semibold flex items-center gap-1">
                                                        {euro(accommodationTotal)}
                                                        <span className="text-xs font-normal text-gray-500 uppercase">
                                                            {t('businessProposal.prices.ttc_without_value')}
                                                        </span>
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                <div className="border-t border-gray-100 my-4" />
                            </div>
                        )}

                        {/* Items */}
                        {selectedItems.length === 0 ? (
                            <p className="text-sm text-gray-500 mb-4">
                                {t('businessProposal.services.noneSelected')}
                            </p>
                        ) : (
                            <ul className="divide-y divide-gray-100">
                                {selectedItems.map((it) => {
                                    return (
                                        <li
                                            key={it.id}
                                            className="py-3 flex items-start gap-3"
                                        >
                                            <div className="flex-1">
                                                <p className="text-sm leading-snug">
                                                    {it.quantity > 1 ? `${it.quantity} x ${it.title}` : it.title}
                                                </p>
                                                {it.note && (
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {it.note}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-sm font-medium whitespace-nowrap text-gray-700">
                                                {(() => {
                                                    const { TTC } = deriveHTTTC({
                                                        price: it.price,
                                                        publicPrice: it.publicPrice,
                                                        vat: it.vat,
                                                        mode: priceMode,
                                                        t,
                                                    });
                                                    const qty = Number.isFinite(Number(it.quantity)) ? Number(it.quantity) : 1;
                                                    const totalLine = (Number.isFinite(TTC) ? TTC * qty : 0);
                                                    return euro(totalLine);
                                                })()}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}

                        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                            <span className="text-base font-semibold flex items-center gap-2">
                                {t('businessProposal.common.currencyTotal')}
                            </span>
                            <span className="text-lg font-bold flex items-center gap-1">
                                {euro(grandTotal)}
                                <span className="text-xs font-normal text-gray-500 uppercase">
                                    {t('businessProposal.prices.ttc_without_value')}
                                </span>
                            </span>
                        </div>

                        {isAccepted ? (
                            <div className="mt-5 space-y-3">
                                {/* Info button: already accepted */}
                                <button
                                    type="button"
                                    disabled
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 font-medium bg-gray-200 text-gray-500 cursor-default"
                                >
                                    {t('businessProposal.summary.alreadyAcceptedCta')}
                                </button>

                                {/* NEW: Go to quotation */}
                                {quotationId &&
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (quotationId) {
                                                navigate(`/quotation/${quotationId}`);
                                            }
                                        }}
                                        disabled={!quotationId}
                                        className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 font-medium  
                ${quotationId ? 'bg-main text-white hover:bg-main-dark' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
            `}
                                    >
                                        {t('businessProposal.summary.goToQuotation')}
                                    </button>
                                }
                            </div>
                        ) : (
                            <button
                                className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 font-medium  
    ${canAccept && !isSaving ? 'bg-main text-white hover:bg-main-dark' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
  `}
                                onClick={canAccept && !isSaving ? handleSaveClick : undefined}
                                disabled={!canAccept || isSaving}
                            >
                                {isSaving
                                    ? t('businessProposal.common.saving')
                                    : (isExpired
                                        ? t('businessProposal.errors.expiredCta')
                                        : t('businessProposal.common.saveSelection')
                                    )
                                }
                            </button>
                        )}
                    </div>
                </aside>
            </main>
        </div>
    );
}